import { useEffect, useState } from 'react';
import { Input } from '@react-apps/design-system';
import type { Subject } from '../../domain/entities/content.entity';
import { useSubjects } from '../hooks/use-subjects';
import { getApiErrorMessage } from '../display';

/** Resultados por busca. Acima disso a tela pede para refinar, não pagina. */
const SEARCH_LIMIT = 20;
const DEBOUNCE_MS = 300;

/**
 * Escolher um assunto **por busca**. Era um `Select` com os 200 primeiros, e
 * assunto fora deles não tinha nome nem podia ser escolhido — com ~milhares de
 * assuntos da carga, isso era a maioria.
 *
 * Não há combobox no DS nem no admin, então o desenho é o mínimo: um `Input`
 * e uma lista de `<button>`, com Escape limpando a busca. A contagem e o estado
 * da busca são anunciados por `aria-live`.
 *
 * Mora dentro de `<form>` no card do preview: Enter não pode submeter o card.
 */
export function SubjectSearch({
  label,
  onPick,
  excludeId,
  disabled,
}: {
  label: string;
  onPick: (subject: Subject) => void;
  /** O assunto atual, que não faz sentido oferecer de novo. */
  excludeId?: string | null;
  disabled?: boolean;
}) {
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(term.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  const active = term.trim() !== '';
  const query = useSubjects(
    { page: 1, perPage: SEARCH_LIMIT, search },
    { enabled: search !== '' },
  );
  const results = (query.data?.items ?? []).filter((s) => s.id !== excludeId);
  const total = query.data?.total ?? 0;

  // Entre a digitação e o fim do debounce, a lista ainda é a do termo antigo:
  // clicável, ela ofereceria um assunto que não casa com o que está no campo.
  const pending = term.trim() !== search || query.isFetching;

  const status = !active
    ? ''
    : pending
      ? 'Buscando…'
      : query.isError
        ? getApiErrorMessage(query.error, 'Falha ao buscar assuntos')
        : results.length === 0
          ? `Nenhum assunto para '${search}'`
          : total > SEARCH_LIMIT
            ? `${total} assuntos — refine a busca`
            : `${results.length} ${results.length === 1 ? 'assunto' : 'assuntos'}`;

  return (
    <div className="space-y-1">
      <Input
        label={label}
        value={term}
        disabled={disabled}
        placeholder="Digite parte do nome…"
        onChange={(e) => setTerm(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
          if (e.key === 'Escape') setTerm('');
        }}
      />
      <p
        aria-live="polite"
        className={`text-xs ${query.isError ? 'text-red-600' : 'text-(--color-text-secondary)'}`}
      >
        {status}
      </p>
      {active && !pending && !query.isError && results.length > 0 && (
        <ul className="border-(--color-border) divide-(--color-border) max-h-64 divide-y overflow-auto rounded-lg border">
          {results.map((subject) => (
            <li key={subject.id}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  onPick(subject);
                  setTerm('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setTerm('');
                }}
                className="text-(--color-text-primary) hover:bg-(--color-bg-secondary) w-full px-3 py-2 text-left text-sm"
              >
                {subject.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
